import React from "react";
import { Phone, ArrowRight, ShieldCheck, Hammer, Star } from "lucide-react";
import { useSite } from "@/context/SiteContext";

export default function Hero() {
  const { site } = useSite();
  const bg = site?.hero_background;

  return (
    <section
      data-testid="hero-section"
      id="top"
      className="relative bg-neutral-950 overflow-hidden"
    >
      {/* Background image + overlay */}
      {bg && (
        <div
          className="absolute inset-0 bg-cover bg-center opacity-30"
          style={{ backgroundImage: `url(${bg})` }}
          aria-hidden
        />
      )}
      <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-black/40" aria-hidden />
      <div className="hero-red-strip" aria-hidden />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          {/* Left: text */}
          <div className="lg:col-span-7 animate-fade-up">
            <div className="inline-flex items-center gap-3 border-2 border-[#DC2626] bg-[#DC2626]/10 px-4 py-2 mb-6">
              <Star size={14} className="text-[#DC2626]" fill="#DC2626" />
              <span className="text-white font-heading text-xs tracking-[0.25em] uppercase">
                {site?.tagline || "Quality Work. Beautiful Results."}
              </span>
            </div>

            <h1
              data-testid="hero-title"
              className="font-heading text-white text-5xl sm:text-6xl lg:text-7xl font-bold uppercase leading-[0.95] tracking-tight"
            >
              {(site?.hero_title || "We Transform Spaces Into Dreams")
                .split(" ")
                .map((w, i, arr) => {
                  const isRed = /dream|dreams|spaces|transform/i.test(w) && i > arr.length - 4;
                  return (
                    <span key={i} className={isRed ? "text-[#DC2626]" : ""}>
                      {w}{i < arr.length - 1 ? " " : ""}
                    </span>
                  );
                })}
            </h1>

            <p className="mt-6 text-lg sm:text-xl text-neutral-300 max-w-xl leading-relaxed">
              {site?.hero_subtitle}
            </p>

            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <a
                data-testid="hero-cta-quote"
                href="#contact"
                className="btn-primary-cta justify-center"
              >
                {site?.hero_cta_primary || "Get Free Quote"}
                <ArrowRight size={18} strokeWidth={2.5} />
              </a>
              <a
                data-testid="hero-cta-call"
                href={`tel:${site?.phone_raw || "+12249669352"}`}
                className="btn-outline-cta justify-center"
              >
                <Phone size={18} strokeWidth={2.5} />
                {site?.phone || "(224) 966-9352"}
              </a>
            </div>

            {/* Trust markers */}
            <div className="mt-12 grid grid-cols-3 gap-4 max-w-lg">
              {[
                { icon: ShieldCheck, label: "Licensed\n& Insured" },
                { icon: Hammer, label: "Expert\nCraftsmanship" },
                { icon: Star, label: "5-Star\nReviews" },
              ].map((t, i) => (
                <div key={i} className="border-l-2 border-[#DC2626] pl-3">
                  <t.icon size={22} className="text-[#DC2626] mb-2" strokeWidth={2.5} />
                  <div className="text-white text-xs font-heading uppercase tracking-widest whitespace-pre-line leading-tight">
                    {t.label}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right: dynamic logo card */}
          <div className="lg:col-span-5 animate-fade-up" style={{ animationDelay: "150ms" }}>
            <div
              data-testid="hero-logo-card"
              className="relative bg-white p-8 sm:p-12 border-4 border-white/10"
              style={{ boxShadow: "12px 12px 0 0 #DC2626" }}
            >
              {site?.logo_url ? (
                <img
                  data-testid="hero-logo-image"
                  src={site.logo_url}
                  alt={site?.business_name || "J&S Complete Remodeling Inc"}
                  className="w-full h-auto max-h-[420px] object-contain"
                />
              ) : (
                <div className="aspect-square bg-neutral-100 flex items-center justify-center text-neutral-400 font-heading">
                  LOGO
                </div>
              )}
              <div className="mt-4 flex items-center justify-between border-t border-neutral-200 pt-4">
                <span className="font-heading uppercase text-xs tracking-widest text-neutral-500">
                  Serving
                </span>
                <span className="font-heading uppercase text-xs tracking-widest text-neutral-900 font-semibold text-right">
                  {site?.service_area || "Chicagoland, IL"}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
